import React, { useState, useEffect } from 'react';
import { filesAPI, projectsAPI } from '../api';

function MyFiles() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isPublic, setIsPublic] = useState(false);
  const [message, setMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [fileVersion, setFileVersion] = useState('');
  const [filePlatform, setFilePlatform] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getMyProjects();
      if (response.data.success) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [filter]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const visibility = filter === 'all' ? null : filter;
      const response = await filesAPI.getMyFiles(visibility);
      setFiles(response.data.files);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploading(true);
    setMessage('');
    setUploadProgress(0);

    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      const response = await filesAPI.upload(selectedFiles, isPublic);
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (response.data.success) {
        const uploadedFiles = response.data.files || [{ file_id: response.data.file_id }];
        
        // If a project is selected, add the first file to it (or all files)
        if (selectedProject && uploadedFiles.length > 0) {
          try {
            // Add first file to project (can be enhanced to add all files)
            const firstFile = uploadedFiles[0];
            await projectsAPI.addDownload(selectedProject, {
              file_id: firstFile.file_id,
              version: fileVersion,
              platform: filePlatform
            });
            setMessage(`${uploadedFiles.length} file(s) uploaded and added to project!`);
          } catch (error) {
            console.error('Error adding to project:', error);
            setMessage(`${uploadedFiles.length} file(s) uploaded, but failed to add to project`);
          }
        } else {
          const count = uploadedFiles.length;
          setMessage(`${count} file(s) uploaded successfully!`);
        }
        
        setTimeout(() => {
          setSelectedFiles([]);
          setIsPublic(false);
          setUploadProgress(0);
          setSelectedProject('');
          setFileVersion('');
          setFilePlatform('');
          setMessage('');
          loadFiles();
        }, 1500);
      }
    } catch (error) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      setMessage(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleVisibility = async (fileId, currentVisibility) => {
    try {
      const newVisibility = currentVisibility === 'public' ? false : true;
      await filesAPI.updateVisibility(fileId, newVisibility);
      loadFiles();
    } catch (error) {
      console.error('Error updating visibility:', error);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await filesAPI.deleteFile(fileId);
      loadFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      setSelectedFiles(droppedFiles);
    }
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length > 0) {
      setSelectedFiles(newFiles);
    }
  };

  const removeSelectedFile = (index) => {
    if (index !== undefined) {
      // Remove specific file
      setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    } else {
      // Remove all files
      setSelectedFiles([]);
      setUploadProgress(0);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    const icons = {
      apk: 'APK',
      ipa: 'IPA',
      exe: 'EXE',
      dmg: 'DMG',
      zip: 'ZIP',
      rar: 'RAR',
      pdf: 'PDF',
      doc: 'DOC',
      docx: 'DOC',
      txt: 'TXT',
      jpg: 'IMG',
      jpeg: 'IMG',
      png: 'IMG',
      gif: 'IMG',
      mp4: 'VID',
      mp3: 'AUD',
    };
    return icons[ext] || 'FILE';
  };

  return (
    <div className="container">
      <h1 className="mb-3">My Files</h1>

      {/* Upload Form */}
      <div className="card mb-3">
        <h3 className="mb-2">Upload File</h3>
        <p className="text-muted text-small mb-3">
          Share any file securely with SharePanda (APK, IPA, EXE, DMG, ZIP, etc.) - Max 2GB
        </p>
        
        {message && (
          <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-error'}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleFileUpload}>
          {!selectedFiles || selectedFiles.length === 0 ? (
            <div
              className={`upload-area ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <div className="upload-icon">📁</div>
              <div className="upload-text">
                <p className="upload-title">
                  {isDragging ? 'Drop your files here' : 'Drag and drop your files here'}
                </p>
                <p className="upload-subtitle">or click to browse (multiple files supported)</p>
              </div>
              <input
                id="fileInput"
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </div>
          ) : (
            <div className="file-preview">
              <div className="file-preview-header">
                <div className="file-preview-info">
                  <span className="file-icon">📄</span>
                  <div className="file-details">
                    <div className="file-name">
                      {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                    </div>
                    <div className="file-size">
                      {formatBytes(selectedFiles.reduce((sum, f) => sum + f.size, 0))} total
                    </div>
                  </div>
                </div>
                {!uploading && (
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => removeSelectedFile()}
                    title="Remove all files"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              {/* List of selected files */}
              <div className="selected-files-list" style={{ marginTop: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                {selectedFiles.map((file, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: '#eae4e9',
                    border: '1px solid #c5cde8',
                    borderRadius: '4px',
                    marginBottom: '4px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <span className="file-icon" style={{ fontSize: '14px' }}>
                        {getFileIcon(file.name)}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#3d3d52' }}>{file.name}</div>
                        <div style={{ fontSize: '12px', color: '#7f7f94' }}>{formatBytes(file.size)}</div>
                      </div>
                    </div>
                    {!uploading && (
                      <button
                        type="button"
                        onClick={() => removeSelectedFile(index)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #c5cde8',
                          cursor: 'pointer',
                          color: '#7f7f94',
                          fontSize: '18px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = '#c96a7a';
                          e.currentTarget.style.borderColor = '#c96a7a';
                          e.currentTarget.style.color = '#f0efeb';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.borderColor = '#c5cde8';
                          e.currentTarget.style.color = '#7f7f94';
                        }}
                        title="Remove file"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <div className="progress-text">{uploadProgress}%</div>
                </div>
              )}
            </div>
          )}

          {selectedFiles && selectedFiles.length > 0 && !uploading && (
            <div>
              <div className="form-group mt-3">
                <label className="form-label">Add to Project (Optional)</label>
                <select
                  className="form-control"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <option value="">-- No Project --</option>
                  {projects.map(project => (
                    <option key={project.project_id} value={project.project_id}>
                      {project.title}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProject && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Version (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={fileVersion}
                      onChange={(e) => setFileVersion(e.target.value)}
                      placeholder="1.0.0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Platform (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={filePlatform}
                      onChange={(e) => setFilePlatform(e.target.value)}
                      placeholder="Android, iOS, Windows, etc."
                    />
                  </div>
                </div>
              )}

              <div className="form-group mt-3">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">
                    <span className="checkbox-title">Make this file public</span>
                    <span className="checkbox-description">
                      Anyone with the link can download this file
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {selectedFiles && selectedFiles.length > 0 && !uploading && (
            <div className="d-flex gap-2 mt-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={selectedFiles.length === 0}
              >
                Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => removeSelectedFile()}
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Filter */}
      <div className="card mb-3">
        <div className="d-flex gap-2">
          <button
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('all')}
          >
            All Files
          </button>
          <button
            className={`btn ${filter === 'public' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('public')}
          >
            Public
          </button>
          <button
            className={`btn ${filter === 'private' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('private')}
          >
            Private
          </button>
        </div>
      </div>

      {/* Files List */}
      <div className="card">
        <h3 className="mb-2">Files ({files.length})</h3>
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : files.length === 0 ? (
          <p className="text-muted">No files found.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Filename</th>
                <th>Size</th>
                <th>Visibility</th>
                <th>Downloads</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.file_id}>
                  <td>{file.filename}</td>
                  <td>{formatBytes(file.size)}</td>
                  <td>
                    <span
                      className={`badge ${
                        file.visibility === 'public'
                          ? 'badge-success'
                          : 'badge-warning'
                      }`}
                    >
                      {file.visibility}
                    </span>
                  </td>
                  <td>{file.downloads || 0}</td>
                  <td>{formatDate(file.uploaded_at)}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <a
                        href={filesAPI.downloadFile(file.file_id)}
                        className="btn btn-primary"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </a>
                      <button
                        onClick={() =>
                          handleToggleVisibility(file.file_id, file.visibility)
                        }
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                      >
                        {file.visibility === 'public' ? 'Make Private' : 'Make Public'}
                      </button>
                      <button
                        onClick={() => handleDeleteFile(file.file_id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default MyFiles;
